/**
 * Shared helpers for assessment questions and related UI.
 * Additional question utilities can be consolidated here over time.
 */

import { titleCase } from 'js/utils/string';
import {
  faList,
  faListCheck,
  faListRadio,
  faMessageLines,
  faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { faList as falList } from '@fortawesome/pro-light-svg-icons';

const DEFAULT_TYPE_ICON = faList;

/** Question types a survey may contain, in menu order. */
const SURVEY_TYPE_ORDER = Object.freeze([
  'long-text',
  'single-select',
  'multi-select',
  'likert',
  'unscored-likert',
]);

/**
 * Question types the live editor offers, in menu order. Sentiment is live-only;
 * the rest match the survey menu.
 */
const LIVE_TYPE_ORDER = Object.freeze([
  'sentiment',
  'long-text',
  'single-select',
  'multi-select',
  'likert',
  'unscored-likert',
]);

const QUESTION_TYPES_BY_KEY = Object.freeze({
  likert: Object.freeze({
    name: 'Scored Likert',
    icon: faList,
  }),
  'unscored-likert': Object.freeze({
    name: 'Informational Likert',
    icon: falList,
  }),
  'multi-select': Object.freeze({
    name: 'Multi-Select',
    icon: faListCheck,
  }),
  'single-select': Object.freeze({
    name: 'Single-Select',
    icon: faListRadio,
  }),
  'long-text': Object.freeze({
    name: 'Comment',
    icon: faMessageLines,
  }),
  sentiment: Object.freeze({
    name: 'Sentiment',
    icon: faThumbsUp,
  }),
});

function normalizedTypeKey(type) {
  if (type === undefined || type === null) {
    return '';
  }
  return String(type).trim();
}

export function getQuestionMeta(type, opts = {}) {
  let key = normalizedTypeKey(type);
  if (!key && opts.defaultType != null) {
    key = normalizedTypeKey(opts.defaultType);
  }
  if (!key) {
    return { name: '', icon: null };
  }

  return (
    QUESTION_TYPES_BY_KEY[key] ?? {
      name: titleCase(key.replace(/-/g, ' ')),
      icon: DEFAULT_TYPE_ICON,
    }
  );
}

const TYPE_ORDER_BY_ASSESSMENT_TYPE = Object.freeze({
  survey: SURVEY_TYPE_ORDER,
  live: LIVE_TYPE_ORDER,
  'live-assessment': LIVE_TYPE_ORDER,
});

function menuFor(order) {
  return Object.freeze(
    order.map((key) => Object.freeze([key, QUESTION_TYPES_BY_KEY[key]]))
  );
}

const MENU_BY_ASSESSMENT_TYPE = Object.freeze(
  Object.fromEntries(
    Object.entries(TYPE_ORDER_BY_ASSESSMENT_TYPE).map(([type, order]) => [
      type,
      menuFor(order),
    ])
  )
);

/**
 * Ordered `[typeKey, meta]` rows for the editor question-type menu, scoped to
 * the assessment being edited. Unknown types fall back to the survey menu.
 */
export function questionTypeMenuFor(assessmentType) {
  return (
    MENU_BY_ASSESSMENT_TYPE[normalizedTypeKey(assessmentType)] ??
    MENU_BY_ASSESSMENT_TYPE.survey
  );
}
